import { useState, useCallback, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import PixelIcon from '@/components/ui/PixelIcon';
import { CARD_DEFINITIONS, RARITY_COLORS, MAX_HAND_SIZE } from '@/config/cards';
import type { CardInstance } from '@/types/card';
import { COLOR, RADIUS, SPACING, FONT_SIZE, BLUR, Z_INDEX } from '@/config/designTokens';

const MAX_DEALT = 4;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CardPickerProps {
  inventory: CardInstance[];
  onConfirm: (selectedIds: string[]) => void;
  onSkip: () => void;
}

// ---------------------------------------------------------------------------
// Checkmark SVG (inline, no emoji)
// ---------------------------------------------------------------------------

function Checkmark() {
  return (
    <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
      <path
        d="M3 8.5L6.5 12L13 4"
        stroke={COLOR.textWhite}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Card Tile
// ---------------------------------------------------------------------------

interface CardTileProps {
  card: CardInstance;
  isSelected: boolean;
  index: number;
  onToggle: (id: string) => void;
}

function CardTile({ card, isSelected, index, onToggle }: CardTileProps) {
  const info = CARD_DEFINITIONS[card.type];
  const baseColor = RARITY_COLORS[card.rarity];

  const handleClick = useCallback(() => {
    onToggle(card.id);
  }, [onToggle, card.id]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{
        opacity: 1,
        y: 0,
        borderColor: isSelected ? COLOR.textWhite : baseColor,
      }}
      transition={{
        delay: index * 0.05,
        duration: 0.25,
        ease: 'easeOut',
        borderColor: { duration: 0.2 },
      }}
      whileTap={{ scale: 0.95 }}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-label={`${info.name} - ${card.rarity}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      style={{
        position: 'relative',
        border: `2px solid ${isSelected ? COLOR.textWhite : baseColor}`,
        borderRadius: RADIUS.lg,
        background: isSelected
          ? 'rgba(255,255,255,0.12)'
          : 'rgba(0,0,0,0.55)',
        padding: '14px 10px 12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        cursor: 'pointer',
        userSelect: 'none',
        minWidth: 0,
      }}
    >
      {/* Selection checkmark badge */}
      <AnimatePresence>
        {isSelected && (
          <motion.div
            key="check"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            style={{
              position: 'absolute',
              top: -8,
              right: -8,
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: COLOR.actionResume,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Checkmark />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Icon */}
      <PixelIcon
        name={info.icon as Parameters<typeof PixelIcon>[0]['name']}
        size={28}
        color={isSelected ? COLOR.textWhite : baseColor}
      />

      {/* Name */}
      <span
        style={{
          color: COLOR.textWhite,
          fontSize: FONT_SIZE.hudPill,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          textAlign: 'center',
          lineHeight: 1.2,
        }}
      >
        {info.name}
      </span>

      {/* Rarity */}
      <span
        style={{
          color: baseColor,
          fontSize: FONT_SIZE.caption,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {card.rarity}
      </span>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// CardPicker
// ---------------------------------------------------------------------------

export default function CardPicker({
  inventory,
  onConfirm,
  onSkip,
}: CardPickerProps) {
  // Deal up to MAX_DEALT cards with unique types from inventory (shuffled)
  const dealtCards = useMemo(() => {
    const shuffled = [...inventory].sort(() => Math.random() - 0.5);
    const seen = new Set<string>();
    const result: CardInstance[] = [];
    for (const card of shuffled) {
      if (seen.has(card.type)) continue;
      seen.add(card.type);
      result.push(card);
      if (result.length >= MAX_DEALT) break;
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory.length]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const toggleCard = useCallback((id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_HAND_SIZE) return prev;
      return [...prev, id];
    });
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm(selectedIds);
  }, [onConfirm, selectedIds]);

  // Keyboard: Enter auto-selects first 3 and confirms, Escape skips
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const autoIds = dealtCards.slice(0, MAX_HAND_SIZE).map(c => c.id);
        onConfirm(autoIds);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onSkip();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dealtCards, onConfirm, onSkip]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: Z_INDEX.pauseOverlay,
        backdropFilter: BLUR.overlay,
        WebkitBackdropFilter: BLUR.overlay,
        background: COLOR.expandedOverlay,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut', delay: 0.05 }}
        style={{
          width: '100%',
          maxWidth: 480,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 20,
        }}
      >
        {/* Title */}
        <div style={{ textAlign: 'center' }}>
          <h2
            style={{
              color: COLOR.textWhite,
              fontSize: FONT_SIZE.modalTitle,
              fontWeight: 700,
              margin: 0,
              letterSpacing: '0.02em',
            }}
          >
            Choose Your Cards
          </h2>
          <p
            style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: FONT_SIZE.hudPill,
              margin: '6px 0 0',
            }}
          >
            (select up to {MAX_HAND_SIZE})
          </p>
        </div>

        {/* Card grid */}
        {dealtCards.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(dealtCards.length, 4)}, 1fr)`,
              gap: 10,
              width: '100%',
              padding: '4px 0',
            }}
          >
            {dealtCards.map((card, i) => (
              <CardTile
                key={card.id}
                card={card}
                isSelected={selectedIds.includes(card.id)}
                index={i}
                onToggle={toggleCard}
              />
            ))}
          </div>
        ) : (
          <span
            style={{
              color: 'rgba(255,255,255,0.35)',
              fontSize: FONT_SIZE.hudPill,
              fontStyle: 'italic',
              padding: '32px 0',
            }}
          >
            No cards in inventory
          </span>
        )}

        {/* Start Level button */}
        <motion.button
          type="button"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.96 }}
          onClick={handleConfirm}
          style={{
            width: '100%',
            maxWidth: 320,
            padding: '14px 24px',
            borderRadius: RADIUS.lg,
            border: '1.5px solid rgba(255,255,255,0.2)',
            background:
              selectedIds.length > 0
                ? 'rgba(74,222,128,0.2)'
                : 'rgba(255,255,255,0.08)',
            color: COLOR.textWhite,
            fontSize: FONT_SIZE.statValue,
            fontWeight: 700,
            cursor: 'pointer',
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
            transition: 'background 0.2s ease',
          }}
        >
          Start Level
          {selectedIds.length > 0 && (
            <span style={{ opacity: 0.7, marginLeft: 6 }}>
              ({selectedIds.length})
            </span>
          )}
        </motion.button>

        {/* Skip link */}
        <motion.button
          type="button"
          whileTap={{ scale: 0.95 }}
          onClick={onSkip}
          style={{
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.4)',
            fontSize: FONT_SIZE.hudPill,
            cursor: 'pointer',
            padding: '4px 12px',
            letterSpacing: '0.02em',
          }}
        >
          Skip
        </motion.button>
      </motion.div>
    </motion.div>
  );
}
